'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useShift } from '@hooks/useShift';
import Accept from '@icons/Accept.svg';
import { Shift, ShiftReactionStatus, TeacherProfile } from 'types/collections';

import { InformationField } from '@components/InformationField';
import { Modal } from '@components/Modal';
import { ShiftInfoCard } from '@components/ShiftInfoCards';
import { showToast } from '@components/ToastContainer';

import { formatDisplayDate, formatDisplayTime } from '@utils/helpers';

interface ShiftApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedShift: Shift | undefined;
  selectedShiftId: string | null;
  schoolName: string | undefined;
  teacher: TeacherProfile | undefined;
}

/**
 * Modal component for teacher shift applications
 */
export const ShiftApplicationModal: React.FC<ShiftApplicationModalProps> = ({
  isOpen,
  onClose,
  selectedShift,
  selectedShiftId,
  schoolName,
  teacher,
}) => {
  const { t } = useTranslation('common');
  const { submitShiftReaction, isSubmittingReaction } = useShift();

  const handleReactToShift = () => {
    if (!selectedShiftId || !teacher?.id) return;

    submitShiftReaction(
      {
        shiftId: selectedShiftId,
        reaction: ShiftReactionStatus.APPLIED,
      },
      {
        onSuccess: () => {
          onClose();
          showToast('success', t('toast.success'), t('toast.successReactToShift'));
        },
        onError: () => {
          showToast('error', t('toast.shiftGenericTitle'), t('toast.shiftGenericError'));
        },
      },
    );
  };

  const modalButtons = [
    {
      label: t('modals.confirmResponse'),
      onClick: handleReactToShift,
      variant: 'primary' as const,
      icon: <Accept />,
      disabled: isSubmittingReaction,
    },
  ];

  return (
    <Modal title={t('modals.assignment')} buttons={modalButtons} isOpen={isOpen} onClose={onClose}>
      {selectedShift ? (
        <>
          <ShiftInfoCard
            shiftId={selectedShiftId || ''}
            group={selectedShift.group || ''}
            date={formatDisplayDate(selectedShift.shift_date || '')}
            timeFrom={formatDisplayTime(selectedShift.time_from || '')}
            timeUntil={formatDisplayTime(selectedShift.time_till || '')}
          />
          <InformationField
            text={t('modals.assignmentInfo', {
              schoolName: schoolName,
            })}
          />
        </>
      ) : null}
    </Modal>
  );
};
